import React, { useState, useEffect } from 'react';
import {
  Autocomplete,
  TextField,
  Button,
  Paper,
  Box,
  Typography,
  Fade,
  useTheme,
  alpha,
  Avatar,
  Chip,
  CircularProgress,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import {
  CompareArrows,
  Search as SearchIcon,
  AccountTree as AccountTreeIcon,
  Error as ErrorIcon,
  CheckCircle as CheckCircleIcon,
} from '@mui/icons-material';
import axios from 'axios';
import API_BASE_URL, { BASE_URL } from '../config';
import { useNavigate } from 'react-router-dom';


// ─── Styled Components ───────────────────────────────────────────────────────

const StyledPaper = styled(Paper)(({ theme }) => ({
  background: `linear-gradient(135deg, ${theme.palette.background.paper} 0%, ${alpha(theme.palette.primary.light, 0.1)} 100%)`,
  borderRadius: theme.spacing(3),
  padding: theme.spacing(4),
  position: 'relative',
  overflow: 'hidden',
  boxShadow: '0 8px 32px rgba(0,0,0,0.1)',
}));

const StyledAutocomplete = styled(Autocomplete)(({ theme }) => ({
  '& .MuiOutlinedInput-root': {
    borderRadius: theme.spacing(2),
    transition: 'all 0.3s ease',
    '&:hover': { backgroundColor: alpha(theme.palette.primary.main, 0.02) },
    '&.Mui-focused': {
      backgroundColor: alpha(theme.palette.primary.main, 0.05),
      '& fieldset': { borderColor: theme.palette.primary.main, borderWidth: '2px' },
    },
  },
}));

const ResultBanner = styled(Box)(({ theme }) => ({
  background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
  borderRadius: theme.spacing(2),
  padding: theme.spacing(3),
  textAlign: 'center',
  color: '#fff',
  marginBottom: theme.spacing(3),
}));

// ─── Connection Path Tree ─────────────────────────────────────────────────────
// Vertical chain: each person card connected by a labeled arrow.
// person1 (top, blue highlight) → intermediates → person2 (bottom, purple highlight)

const ConnectionPathTree = ({ connectingNodes, highlightIds = [] }) => {
  const theme = useTheme();
  if (!connectingNodes || connectingNodes.length < 1) return null;

  const highlightSet = new Set(highlightIds.map(String));

  // Determine which highlighted node is first vs last in the display order
  const firstHighlightIdx = connectingNodes.findIndex(n => highlightSet.has(String(n.id)));
  const lastHighlightIdx  = [...connectingNodes].reverse().findIndex(n => highlightSet.has(String(n.id)));
  const lastHighlightActualIdx = connectingNodes.length - 1 - lastHighlightIdx;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 2 }}>
      {connectingNodes.map((node, idx) => {
        const isHighlighted = highlightSet.has(String(node.id));
        const isPerson1     = isHighlighted && idx === firstHighlightIdx;
        const isPerson2     = isHighlighted && idx === lastHighlightActualIdx && idx !== firstHighlightIdx;

        const imgSrc = node.imageUrl
          ? node.imageUrl.startsWith('http') ? node.imageUrl : `${BASE_URL}${node.imageUrl}`
          : null;

        const borderColor = isPerson1
          ? theme.palette.primary.main
          : isPerson2
          ? theme.palette.secondary.main
          : isHighlighted
          ? theme.palette.primary.light
          : theme.palette.divider;

        const bgColor = isPerson1
          ? alpha(theme.palette.primary.light, 0.18)
          : isPerson2
          ? alpha(theme.palette.secondary.light, 0.18)
          : theme.palette.background.paper;

        const avatarBg = isPerson1
          ? 'primary.main'
          : isPerson2
          ? 'secondary.main'
          : String(node.gender || '').toLowerCase() === 'female'
          ? 'secondary.light'
          : 'grey.400';

        // The connector chip label: what the PREVIOUS node's relation to THIS node is.
        // API sends relationToNext on each node = "what this node IS to the next node".
        // So for the connector above node[idx], we use connectingNodes[idx-1].relationToNext
        const connectorLabel = idx > 0
          ? (connectingNodes[idx - 1].relationToNext || '')
          : null;

        return (
          <React.Fragment key={`node-${node.id ?? idx}`}>
            {/* ── Connector line + relation label ── */}
            {idx > 0 && (
              <Box sx={{
                display: 'flex', flexDirection: 'column',
                alignItems: 'center', my: 0.5, gap: 0,
              }}>
                <Box sx={{ width: 2, height: 20, background: theme.palette.primary.main, borderRadius: 1 }} />
                {connectorLabel ? (
                  <Chip
                    label={connectorLabel}
                    size="small"
                    sx={{
                      fontSize: '0.68rem', height: 22, fontWeight: 700,
                      bgcolor: alpha(theme.palette.primary.main, 0.13),
                      color: theme.palette.primary.dark,
                      my: 0.25,
                    }}
                  />
                ) : (
                  <Box sx={{ height: 6 }} />
                )}
                <Box sx={{ width: 2, height: 20, background: theme.palette.primary.main, borderRadius: 1 }} />
              </Box>
            )}

            {/* ── Person card ── */}
            <Box sx={{
              display: 'flex', alignItems: 'center', gap: 2,
              px: 2.5, py: 1.5, borderRadius: 3,
              border: `2px solid ${borderColor}`,
              background: bgColor,
              minWidth: 240,
              maxWidth: 320,
              boxShadow: isHighlighted ? 4 : 1,
              transition: 'all 0.2s',
            }}>
              <Avatar src={imgSrc} sx={{ width: 46, height: 46, bgcolor: avatarBg, flexShrink: 0 }}>
                {(node.name || '?').charAt(0).toUpperCase()}
              </Avatar>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="subtitle2" fontWeight={700} noWrap>
                  {node.name}
                </Typography>
                {/* Show the relation label under the name (what this person IS) */}
                {node.relationship && node.relationship !== 'Start' && (
                  <Typography variant="caption" color="text.secondary" display="block">
                    {node.relationship}
                  </Typography>
                )}
                <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mt: 0.3 }}>
                  {isPerson1 && (
                    <Chip label="Person 1" size="small" color="primary"
                      sx={{ height: 16, fontSize: '0.58rem' }} />
                  )}
                  {isPerson2 && (
                    <Chip label="Person 2" size="small" color="secondary"
                      sx={{ height: 16, fontSize: '0.58rem' }} />
                  )}
                </Box>
              </Box>
            </Box>
          </React.Fragment>
        );
      })}
    </Box>
  );
};

// ─── Main Component ───────────────────────────────────────────────────────────

const UserSearch = () => {
  const theme   = useTheme();
  const navigate = useNavigate();

  // Search inputs
  const [user1, setUser1] = useState('');
  const [user2, setUser2] = useState('');
  const [uid1,  setUid1]  = useState('');
  const [uid2,  setUid2]  = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [isSearchEnabled, setIsSearchEnabled] = useState(false);

  // Result state
  const [result,      setResult]      = useState(null);   // full API response
  const [searchError, setSearchError] = useState('');
  const [loading,     setLoading]     = useState(false);

  // ── Fetch user details on mount ───────────────────────────────────────────

  useEffect(() => {
    const userUid = localStorage.getItem('uid');

    if (!userUid) {
      navigate('/login', { replace: true });
    }
  }, [navigate]);


  // ── Search suggestions ────────────────────────────────────────────────────

  const handleSearch = async (field, value) => {
    if (!value) { setSuggestions([]); return; }
    try {
      const res = await axios.get(`${API_BASE_URL}/search`, { params: { [field]: value } });
      setSuggestions(res.data?.length > 0 ? res.data : []);
    } catch {
      setSuggestions([]);
    }
  };

  const handleFieldChange = (index, value) => {
    if (index === 0) { setUser1(value); setIsSearchEnabled(!!(value && user2)); }
    else             { setUser2(value); setIsSearchEnabled(!!(user1 && value)); }
  };

  const handleSuggestionSelect = (index, opt) => {
    if (index === 0) {
      setUid1(opt.uid);
      setUser1(`${opt.first_name} (UID: ${opt.uid})`);
    } else {
      setUid2(opt.uid);
      setUser2(`${opt.first_name} (UID: ${opt.uid})`);
    }
  };

  // ── Find relation ──────────────────────────────────────────────────────────
  const handleSearchClick = async () => {
    if (!uid1 || !uid2) {
      setSearchError('Please select both members from the dropdown suggestions.');
      return;
    }
    if (uid1 === uid2) {
      setSearchError('Please select two different members.');
      return;
    }

    try {
      setLoading(true);
      setSearchError('');
      setResult(null);

      const userUid = localStorage.getItem('uid');
      if (!userUid) { navigate('/login'); return; }

      const res = await axios.post(
        `${API_BASE_URL}/find-relationship`,
        { uid1, uid2 },
        { withCredentials: true }
      );

      if (!res.data.success) {
        setSearchError(res.data.message || 'No relation found.');
      } else {
        setResult(res.data);
      }
    } catch (err) {
      const status = err.response?.status;

      if (status === 401) {
        navigate('/login', { replace: true });
      } else if (status === 400) {
        setSearchError('Please fill all required fields.');
      } else if (status === 404) {
        setSearchError('No relationship found between the selected users.');
      } else {
        setSearchError('Something went wrong. Please try again.');
      }

      setResult(null);
    } finally {
      setLoading(false);
    }
  };

  // ── Build path nodes from API response ────────────────────────────────────
  // connectingNodes from API: [{ id, name, gender, imageUrl, relationToNext, relationship }, ...]
  // Already ordered senior → junior by the backend controller.

  const buildPathNodes = (res) => {
    if (!res?.connectingNodes?.length) return [];
    return res.connectingNodes.map((n, idx) => ({
      id:            n.id,
      name:          n.name          || 'Unknown',
      gender:        n.gender        || '',
      imageUrl:      n.imageUrl      || n.image_url || null,
      relationToNext: n.relationToNext || null,
      relationship:  n.relationship  || (idx === 0 ? 'Start' : ''),
    }));
  };

  const pathNodes    = buildPathNodes(result);
  // highlightIds = the two originally searched users (always person1 and person2)
  const highlightIds = result?.highlightIds || [];

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <Box>
      <Fade in timeout={1000}>
        <StyledPaper>

          {/* ── Header ── */}
          <Box sx={{ textAlign: 'center', mb: 6 }}>
            <Typography
              variant="h4" gutterBottom
              sx={{
                fontWeight: 700,
                background: `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
                backgroundClip: 'text', WebkitBackgroundClip: 'text', color: 'transparent',
              }}
            >
              Find Family Relations
            </Typography>
            <Typography variant="h6" color="text.secondary">
              Discover how family members are connected
            </Typography>
          </Box>

          {/* ── Search inputs ── */}
          <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 3, mb: 4 }}>
            <StyledAutocomplete
              freeSolo fullWidth
              options={suggestions.map(o => `${o.first_name} (UID: ${o.uid})`)}
              inputValue={user1}
              onInputChange={(_, v) => {
                handleSearch('first_name', v);
                handleFieldChange(0, v);
              }}
              onChange={(_, v) => {
                const opt = suggestions.find(o => `${o.first_name} (UID: ${o.uid})` === v);
                if (opt) handleSuggestionSelect(0, opt);
              }}
              renderInput={(params) => (
                <TextField
                  {...params} label="Select First Person" placeholder="Start typing a name…"
                  InputProps={{ ...params.InputProps, startAdornment: <SearchIcon color="action" sx={{ mr: 1 }} /> }}
                />
              )}
            />

            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', px: 2 }}>
              <CompareArrows
                sx={{
                  transform: { xs: 'rotate(90deg)', md: 'rotate(0deg)' },
                  color: 'primary.main', fontSize: 30,
                }}
              />
            </Box>

            <StyledAutocomplete
              freeSolo fullWidth
              options={suggestions.map(o => `${o.first_name} (UID: ${o.uid})`)}
              inputValue={user2}
              onInputChange={(_, v) => {
                handleSearch('first_name', v);
                handleFieldChange(1, v);
              }}
              onChange={(_, v) => {
                const opt = suggestions.find(o => `${o.first_name} (UID: ${o.uid})` === v);
                if (opt) handleSuggestionSelect(1, opt);
              }}
              renderInput={(params) => (
                <TextField
                  {...params} label="Select Second Person" placeholder="Start typing a name…"
                  InputProps={{ ...params.InputProps, startAdornment: <SearchIcon color="action" sx={{ mr: 1 }} /> }}
                />
              )}
            />
          </Box>

          {/* ── Find button ── */}
          <Box sx={{ textAlign: 'center', mb: 4 }}>
            <Button
              onClick={handleSearchClick}
              variant="contained" color="primary"
              disabled={!isSearchEnabled || loading}
              startIcon={loading ? <CircularProgress size={18} color="inherit" /> : <AccountTreeIcon />}
              sx={{ borderRadius: 8, px: 4, py: 1, fontSize: '1.1rem' }}
            >
              {loading ? 'Finding…' : 'Find Relation'}
            </Button>
          </Box>

          {/* ── Error ── */}
          {searchError && (
            <Box sx={{
              textAlign: 'center', my: 3, p: 2,
              bgcolor: alpha(theme.palette.error.main, 0.1), borderRadius: 2,
            }}>
              <ErrorIcon color="error" sx={{ fontSize: 40, mb: 1 }} />
              <Typography color="error.main" variant="h6">{searchError}</Typography>
            </Box>
          )}

          {/* ── Result ── */}
          {result && (
            <>
              {/* Banner — uses the correct message from backend */}
              <ResultBanner>
                <CheckCircleIcon sx={{ fontSize: 38, mb: 0.5 }} />
                <Typography variant="h5" fontWeight={700} sx={{ mt: 0.5 }}>
                  {result.message}
                </Typography>
              </ResultBanner>

              {/* Connection path tree */}
              {pathNodes.length >= 2 && (
                <Paper sx={{
                  p: 3, borderRadius: 3, mt: 1,
                  background: 'rgba(255,255,255,0.95)',
                  border: `1px solid ${theme.palette.divider}`,
                }}>
                  {/* Sub-title */}
                  <Typography
                    variant="h6" fontWeight="bold" textAlign="center"
                    sx={{
                      mb: 0.5,
                      background: 'linear-gradient(45deg, #1976d2, #7b1fa2)',
                      backgroundClip: 'text', WebkitBackgroundClip: 'text', color: 'transparent',
                    }}
                  >
                    Connection Path
                  </Typography>
                  <Typography variant="body2" color="text.secondary" textAlign="center" sx={{ mb: 2 }}>
                    {result.message}
                  </Typography>

                  <ConnectionPathTree
                    connectingNodes={pathNodes}
                    highlightIds={highlightIds}
                  />
                </Paper>
              )}
            </>
          )}

        </StyledPaper>
      </Fade>
    </Box>
  );
};

export default UserSearch;